<template>
  <!--
    DateRangeFilter — Period + From + To, the one date-range control of the app
    (Maurizio, 2026-09-24: presets "app-wide … share the same functions").
    Picking a period fills From/To (Dubai days, utils/datePresets.js); typing a
    date by hand clears the period so the box never names a period the dates
    are not. Emits `change` once per user action, after From/To are updated.
  -->
  <div class="d-flex flex-wrap align-center ga-2" :class="{ 'flex-column align-stretch': stacked }">
    <v-select
      :model-value="currentPeriod"
      :items="items"
      item-title="text"
      item-value="value"
      :label="periodLabel"
      :density="density"
      variant="outlined"
      hide-details
      :clearable="clearable"
      :min-width="stacked || fluid ? undefined : 170"
      :max-width="stacked || fluid ? undefined : 210"
      :class="{ 'flex-1-1-0': fluid && !stacked }"
      @update:model-value="onPeriod"
    />
    <v-text-field
      :model-value="from"
      type="date"
      :min="min"
      :max="max"
      :label="fromLabel"
      :density="density"
      variant="outlined"
      hide-details
      :clearable="clearable"
      :min-width="stacked || fluid ? undefined : 150"
      :max-width="stacked || fluid ? undefined : 180"
      :class="{ 'flex-1-1-0': fluid && !stacked }"
      @update:model-value="(v) => onDate('from', v)"
    />
    <v-text-field
      :model-value="to"
      type="date"
      :min="min"
      :max="max"
      :label="toLabel"
      :density="density"
      variant="outlined"
      hide-details
      :clearable="clearable"
      :min-width="stacked || fluid ? undefined : 150"
      :max-width="stacked || fluid ? undefined : 180"
      :class="{ 'flex-1-1-0': fluid && !stacked }"
      @update:model-value="(v) => onDate('to', v)"
    />
  </div>
</template>

<script setup>
/**
 * Props / models:
 *   v-model:from, v-model:to   YYYY-MM-DD strings ("" or null = open end)
 *   v-model:period             optional — the chosen period key, for pages
 *                              that keep it (else it is held internally)
 *   periods                    period keys to offer (default: the standard 8);
 *                              "all" clears both dates
 *   stacked                    one field per line (dialogs, narrow panels)
 *   fluid                      fill the width it is given (search panels) instead of fixed widths
 *   min / max                  earliest / latest date the fields accept
 * Events: change({ from, to, period }) after each user action.
 */
import { ref, computed, watch } from "vue";
import { DEFAULT_PERIODS, periodItems, periodRange, periodMatching } from "@/utils/datePresets.js";

const from = defineModel("from", { default: "" });
const to = defineModel("to", { default: "" });
const period = defineModel("period", { default: undefined });

const props = defineProps({
  periods: { type: Array, default: () => DEFAULT_PERIODS },
  fromLabel: { type: String, default: "From" },
  toLabel: { type: String, default: "To" },
  periodLabel: { type: String, default: "Period" },
  density: { type: String, default: "compact" },
  clearable: { type: Boolean, default: true },
  stacked: { type: Boolean, default: false },
  // Fill the space it is given (search panels) instead of its fixed 170/150px widths
  fluid: { type: Boolean, default: false },
  // Earliest / latest date the fields accept (YYYY-MM-DD), e.g. max = today.
  min: { type: String, default: undefined },
  max: { type: String, default: undefined },
});
const emit = defineEmits(["change"]);

const items = computed(() => periodItems(props.periods));

// The period shown: the page's v-model when it keeps one, else our own —
// seeded from the dates, so a page that opens on "this month" shows it.
const internal = ref(periodMatching(from.value, to.value, props.periods));
const currentPeriod = computed(() => (period.value !== undefined ? period.value : internal.value));
function setPeriod(v) {
  internal.value = v;
  if (period.value !== undefined) period.value = v;
}

// Dates changed from outside (a page's Clear button, a restored filter):
// keep the period box truthful.
watch([from, to], ([f, t]) => {
  const p = currentPeriod.value;
  if (!p) return;
  const [pf, pt] = periodRange(p);
  if ((f || null) !== pf || (t || null) !== pt) setPeriod(null);
});

function onPeriod(key) {
  setPeriod(key || null);
  if (key) {
    const [f, t] = periodRange(key);
    from.value = f || "";
    to.value = t || "";
  }
  emit("change", { from: from.value, to: to.value, period: key || null });
}

function onDate(which, v) {
  if (which === "from") from.value = v || "";
  else to.value = v || "";
  // the watch above clears the period if the dates no longer match it
  emit("change", { from: from.value, to: to.value, period: currentPeriod.value });
}
</script>
